// services/authService.ts
import api from "./api";

export interface LoginCredentials {
  email_or_username: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  password_confirmation: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  user_id: number;
  reset_token: string;
  password: string;
  password_confirmation: string;
}

const authService = {
  login: async (email_or_username: string, password: string) => {

    try {
      const response = await api.post("/login", {
        email_or_username,
        password,
      });

      if (response.data.access_token) {
        localStorage.setItem("token", response.data.access_token);
      }
      return response.data;
    } catch (error: any) {
      // Check if it's an unverified email error (403 with email_verified: false)
      if (
        error.response?.status === 403 &&
        error.response?.data?.email_verified === false
      ) {
        // eslint-disable-next-line
        throw {
          isEmailVerificationRequired: true,
          email: error.response.data.email,
          message: error.response.data.message,
        };
      }
      throw error;
    }
  },

  register: async (userData: RegisterData) => {
    try {
      console.log("Sending registration request:", userData);
      const response = await api.post("/register", userData);
      console.log("Registration API response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Registration API error:", error.response?.data);
      throw error;
    }
  },

  verifyEmail: async (email: string, otp: string) => {
    const response = await api.post("/verify-email", { email, otp });
    return response.data;
  },

  resendVerification: async (email: string) => {
    const response = await api.post("/resend-verification", { email });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post("/forgot-password", { email });
    return response.data;
  },

  verifyPasswordResetOtp: async (email: string, otp: string) => {
    const response = await api.post("/verify-password-reset-otp", {
      email,
      otp,
    });
    return response.data;
  },

  resetPassword: async (resetData: ResetPasswordData) => {
    const response = await api.post("/reset-password", resetData);
    return response.data;
  },

  logout: async () => {
    try {
      await api.post("/logout");
    } catch (error) {
      // Continue with logout even if API call fails
      console.log("Logout API error:", error);
    } finally {
      localStorage.removeItem("token");
    }
  },

  checkAuth: async () => {
    try {
      const response = await api.get("/me");
      return response.data;
    } catch (error: any) {
      console.error("Check auth error:", error);
      // Don't throw for 401 errors - just return null
      if (error.response?.status === 401) {
        return null;
      }
      throw error;
    }
  },

  changePassword: async (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string
  ) => {
    const response = await api.post("/change-password", {
      current_password: currentPassword,
      password: newPassword,
      password_confirmation: confirmPassword,
    });
    return response.data;
  },
};

export default authService;
